import { useConnectionAction } from "./useConnectionAction";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { toast } from "sonner";
import { Button } from "@components/ui/button";
import { createIntegrationConnection, updateIntegrationConnection, type IntegrationConnection } from "../../lib/integrations";
import { FormField } from "./FormField";
import { githubFormSchema, type GitHubForm } from "./integrationFormSchemas";

export function GitHubConnectionCard({ connection, onSaved, onCancel }: { connection?: IntegrationConnection; onSaved: () => Promise<void>; onCancel: () => void }) {
  const action = useConnectionAction();
  const isSubmitting = action.pending;
  const formSchema = githubFormSchema.superRefine((values, ctx) => {
    if (!connection && values.token.trim().length < 16) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["token"], message: "Fine-grained token must contain at least 16 characters." });
    }
  });
  const { register, handleSubmit, formState: { errors } } = useForm<GitHubForm>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: connection?.name ?? "GitHub",
      token: "",
      owner: connection?.configSummary.owner ?? "",
      repository: connection?.configSummary.repository ?? "",
      labels: connection?.configSummary.labels?.join(", ") ?? "io-spoke",
    },
  });

  const submit = handleSubmit((values) => action.run("save", async () => {
    const config = {
      owner: values.owner,
      repository: values.repository,
      labels: values.labels.split(",").map((value) => value.trim()).filter(Boolean).slice(0, 10),
      ...(values.token ? { token: values.token } : {}),
    };
    try {
      if (connection) await updateIntegrationConnection(connection.id, { name: values.name, config });
      else await createIntegrationConnection({ provider: "github", name: values.name, config: { ...config, token: values.token }, mappings: {} });
      toast.success(connection ? "GitHub settings updated." : "GitHub connected.");
      await onSaved();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to connect GitHub.");
    }
  }));

  return (
    <form className="space-y-4" onSubmit={submit} noValidate>
      <div className="rounded-lg border bg-muted/30 p-3 text-sm text-muted-foreground">
        Use a fine-grained token limited to this repository with Issues read/write permission. IO Spoke encrypts it and never returns it.
      </div>
      <FormField id="github-name" label="Connection name" registration={register("name")} error={errors.name} />
      <FormField id="github-owner" label="Repository owner" registration={register("owner")} error={errors.owner} />
      <FormField id="github-repository" label="Repository name" registration={register("repository")} error={errors.repository} />
      <FormField id="github-labels" label="Labels for created issues (comma-separated)" registration={register("labels")} error={errors.labels} />
      <FormField
        id="github-token"
        label={`Fine-grained token ${connection ? "(leave blank to keep current)" : ""}`}
        registration={register("token")}
        error={errors.token}
        type="password"
        autoComplete="new-password"
      />
      <div className="flex flex-wrap gap-2">
        <Button type="submit" disabled={isSubmitting} loading={action.saving} loadingLabel={"Saving…"}>
          {connection ? "Save settings" : "Connect GitHub"}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
